/**
 * @module app.um
 */

import { Disposable, autoinject, PLATFORM } from 'aurelia-framework';
import { NavModel, RouteConfig, Router, RouterConfiguration } from 'aurelia-router';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { EventAggregator } from 'aurelia-event-aggregator';



@autoinject()
export class ManageAuthsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private subRouter: Router | null = null;
    protected isAuthAttachToDocument: boolean = false;
    protected params: any;

    ///****************** Event Subscriptions **************************************////
    private navigationCompletedEvent: Disposable | null = null;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(params: any, routeConfig: RouteConfig): Promise<void> {
        this.params = params;

        this.isAuthAttachToDocument = params.faxDocumentId != null;
    }

    protected attached(): void {
        this.checkIfCorrectRoute();

        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.checkIfCorrectRoute());
    }

    protected detached(): void {
        if(this.navigationCompletedEvent) {
            this.navigationCompletedEvent.dispose();
        }
    }

    ////****************** Functions ************************************************////
    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();

        const root = router.generate('um.manage-auths');
        const routes: IAppRouteConfig[] = [
            //{ route: [''], redirect: 'search' },
            // Tab Navigation
            {
                route: 'create/:memberId?', href: `${root}/create`, name: 'um.manage-auths.create', moduleId: PLATFORM.moduleName('app/um/pages/manage-auths/create-auth-page'),
                title: 'um.manageAuths.create', nav: true, activationStrategy: 'invoke-lifecycle',
                settings: { topic: 'um.manage-auths.create', icon: 'ui:icons.create', isVisible: this.accountContext.hasRouteAccess('um.manage-auths.create') }
            },
            {
                route: 'search', name: 'um.manage-auths.search', moduleId: PLATFORM.moduleName('app/um/pages/manage-auths/auth-search-page'),
                title: 'um.manageAuths.search', nav: true,
                settings: { topic: 'um.manage-auths.search', icon: 'ui:icons.search', isDefaultRedirect: true, isVisible: this.accountContext.hasRouteAccess('um.manage-auths.search') }
            },
            {
                route: 'my-auths', name: 'um.manage-auths.my-auths', moduleId: PLATFORM.moduleName('app/um/pages/manage-auths/my-auths-page'),
                title: 'um.manageAuths.my', nav: true,
                settings: { topic: 'um.manage-auths.my-auths', icon: 'ui:icons.list', isVisible: this.accountContext.hasRouteAccess('um.manage-auths.my-auths') }
            },
            {
                route: 'assign', name: 'um.manage-auths.assign', moduleId: PLATFORM.moduleName('app/um/pages/manage-auths/assign-auths-page'),
                title: 'um.manageAuths.assign', nav: true,
                settings: { topic: 'um.manage-auths.assign', icon: 'ui:icons.assign', isVisible: this.accountContext.hasRouteAccess('um.manage-auths.assign') }
            }
        ];

        // Determine default tab
        let defaultRoute: IAppRouteConfig | null = routes.find((r) => r.settings?.isDefaultRedirect === true && r.settings?.isVisible === true) || null;
        //If user doesn't have access to default tab, find first route user has access to and make that the default.
        if(!defaultRoute) {
            const visibleRoutes: IAppRouteConfig[] = routes.filter((r) => { return r.settings?.isVisible === true; });
            defaultRoute = visibleRoutes[0];
            if(!defaultRoute) {
                this.logger.error('User has no valid permissions on this page!');
                return;
            }
        }

        // Put default route as first route
        routes.unshift({ route: [''], redirect: <string>defaultRoute.route });
        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');
        this.subRouter = router;
    }

    protected reset(href: string): true | void {
        if(href == location.hash) {
            this.subRouter!.navigate(href, { replace: true, trigger: true });
        }
        else return true;
    }

    protected checkIfCorrectRoute() {
        this.params = this.subRouter?.currentInstruction?.queryParams;

        this.isAuthAttachToDocument = this.params.faxDocumentId != null;

        // If attaching existing document to auth, make sure only the create auth page is active
        if(this.isAuthAttachToDocument && this.subRouter?.currentInstruction?.config?.name !== 'um.manage-auths.create') {
            this.subRouter!.navigateToRoute('um.manage-auths.create', this.params, { replace: true, trigger: true });
        }
    }

    protected navigationTabs(): NavModel[] {
        let returnTabs: Array<NavModel> = [];
        if(this.subRouter && this.isAuthAttachToDocument) {
            if(this.isAuthAttachToDocument) {
                returnTabs = this.subRouter.navigation.filter(nav => nav.config.name === 'um.manage-auths.create');
            }
            else {
                returnTabs = this.subRouter.navigation;
            }
        }
        return returnTabs;
    }

}
